const fontFamily = [
  '"Inter Variable"',
  '"Inter"',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
  '"Apple Color Emoji"',
  '"Segoe UI Emoji"',
  '"Segoe UI Symbol"',
].join(',')

const weights = {
  regular: 400,
  medium: 500,
  semiBold: 600,
  bold: 700,
} as const

const styles = {
  'display-h1': {
    fontWeight: weights.bold,
    fontSize: '2rem',
    lineHeight: '2.75rem',
    letterSpacing: '-0.45px',
  },
  'display-h2': {
    fontWeight: weights.bold,
    fontSize: '1.5rem',
    lineHeight: '2.375rem',
    letterSpacing: '-0.45px',
  },
  'display-h3': {
    fontWeight: weights.semiBold,
    fontSize: '1.25rem',
    lineHeight: '2rem',
    letterSpacing: '0',
  },
  'display-h4': {
    fontWeight: weights.semiBold,
    fontSize: '1rem',
    lineHeight: '1.5rem',
    letterSpacing: '0',
  },
  'upsized-regular': {
    fontWeight: weights.regular,
    fontSize: '1rem',
    lineHeight: '1.75rem',
    letterSpacing: '0',
  },
  'upsized-medium': {
    fontWeight: weights.medium,
    fontSize: '1rem',
    lineHeight: '1.75rem',
    letterSpacing: '0',
  },
  'upsized-semiBold': {
    fontWeight: weights.semiBold,
    fontSize: '1rem',
    lineHeight: '1.75rem',
    letterSpacing: '0',
  },
  'upsized-bold': {
    fontWeight: weights.bold,
    fontSize: '1rem',
    lineHeight: '1.75rem',
    letterSpacing: '0',
  },
  'body-regular': {
    fontWeight: weights.regular,
    fontSize: '0.875rem',
    lineHeight: '1.5rem',
    letterSpacing: '0',
  },
  'body-medium': {
    fontWeight: weights.medium,
    fontSize: '0.875rem',
    lineHeight: '1.5rem',
    letterSpacing: '0',
  },
  'body-semiBold': {
    fontWeight: weights.semiBold,
    fontSize: '0.875rem',
    lineHeight: '1.5rem',
    letterSpacing: '0',
  },
  'body-bold': {
    fontWeight: weights.bold,
    fontSize: '0.875rem',
    lineHeight: '1.5rem',
    letterSpacing: '0',
  },
  'small-regular': {
    fontWeight: weights.regular,
    fontSize: '0.75rem',
    lineHeight: '1.125rem',
    letterSpacing: '0',
  },
  'small-medium': {
    fontWeight: weights.medium,
    fontSize: '0.75rem',
    lineHeight: '1.125rem',
    letterSpacing: '0',
  },
  'small-bold': {
    fontWeight: weights.semiBold,
    fontSize: '0.75rem',
    lineHeight: '1.125rem',
    letterSpacing: '0',
  },
  'small-uppercase': {
    fontWeight: weights.medium,
    fontSize: '0.625rem',
    lineHeight: '1rem',
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },
  'extraSmall-semiBold': {
    fontWeight: weights.semiBold,
    fontSize: '0.5625rem',
    lineHeight: '1rem',
    letterSpacing: '0.45px',
  },
  'extraSmall-medium': {
    fontWeight: weights.medium,
    fontSize: '0.5625rem',
    lineHeight: '1rem',
    letterSpacing: '0.45px',
  },
} as const

// The token variants only — the legacy/built-in mappings below stay out of the
// augmented `variant` union so they aren't offered at call sites.
export type TokenTypographyVariants = typeof styles

// Theme1 typography variants mapped onto theme2 equivalents. See
// `../typography-migration.md` for usage counts and the rationale behind each
// mapping. Goal: every theme1 variant resolves to *something* so call sites
// can keep compiling while the migration runs.
const legacyStyles = {
  // ─── No good match (preserve original via override) ────────────────────
  HeaderSubtitleXSmallRegular: {
    ...styles['small-regular'],
    fontSize: '0.625rem',
  },
  Tooltip: {
    ...styles['small-regular'],
    fontSize: '0.625rem',
  },
  // theme2 has no 18px tier — recreate from the 16px upsized family.
  H6Regular: {
    ...styles['upsized-regular'],
    fontSize: '1.125rem',
  },
  Body18pxRegular: {
    ...styles['upsized-regular'],
    fontSize: '1.125rem',
  },
  Body18pxMedium: {
    ...styles['upsized-medium'],
    fontSize: '1.125rem',
  },
  // theme2 has no 300 weight — recreate from body-regular.
  HeaderSubtitleMediumLight: {
    ...styles['body-regular'],
    fontWeight: 300,
  },
  // theme2 has no 24–25px regular/medium — recreate from display-h2 (24/700).
  H5Regular: {
    ...styles['display-h2'],
    fontSize: '1.5625rem',
    fontWeight: weights.regular,
  },
  H5Medium: {
    ...styles['display-h2'],
    fontSize: '1.5625rem',
    fontWeight: weights.medium,
  },
} as const

// ─── MUI built-in variants ───────────────────────────────────────────────
// Aliases of the nearest theme2 style, kept defined because MUI reads the
// built-in variants internally (the DataGrid reads caption, ListItemText reads
// body1). `TypographyPropsVariantOverrides` blocks them at our call sites.
const builtinStyles = {
  h1: styles['display-h1'],
  h2: styles['display-h1'],
  h3: styles['display-h2'],
  h4: styles['display-h3'],
  h5: styles['display-h4'],
  h6: styles['body-semiBold'],
  subtitle1: styles['upsized-regular'],
  subtitle2: styles['body-medium'],
  body1: styles['body-regular'],
  body2: styles['body-regular'],
  caption: styles['small-regular'],
  button: styles['body-medium'],
  overline: styles['small-regular'],
}

export const typography = {
  fontFamily,
  weights,
  ...styles,
  ...legacyStyles,
  ...builtinStyles,
} as const

export const MuiTypography = {
  defaultProps: {
    variantMapping: {
      'display-h1': 'h1',
      'display-h2': 'h2',
      'display-h3': 'h3',
      'display-h4': 'h4',
    },
  },
}
